import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { 
    Typography, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Button,
    SelectChangeEvent,
    Box
} from '@mui/material';
import { desiredCities } from '../../constants/consts';

interface CustomChordGroup extends d3.ChordGroup {
    angle?: number;
}

interface CorrelationData {
    [key: string]: { [key: string]: number };
}

interface ChordDiagramProps {
    correlationData: CorrelationData;
}

const DEFAULT_CITIES = ["Ankara", "İstanbul", "İzmir", "Bursa", "Antalya"];

export const ChordDiagram: React.FC<ChordDiagramProps> = ({ correlationData }) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [selectedCities, setSelectedCities] = useState<string[]>(DEFAULT_CITIES);
    const [displayedCities, setDisplayedCities] = useState<string[]>(DEFAULT_CITIES);

    const handleCityChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        if (Array.isArray(value) && value.length >= 2 && value.length <= 5) {
            setSelectedCities(value);
        }
    };

    const handleApplySelection = () => {
        if (selectedCities.length > 0) {
            setDisplayedCities(selectedCities);
        }
    };

    useEffect(() => {
        if (!svgRef.current || Object.keys(correlationData).length === 0 || displayedCities.length === 0) return;

        const matrix = displayedCities.map(city1 => 
            displayedCities.map(city2 => 
                city1 === city2 ? 0 : (correlationData[city1][city2] || 0)
            )
        );

        d3.select(svgRef.current).selectAll("*").remove();

        const containerWidth = 340;
        const containerHeight = 340;
        const width = containerWidth;
        const height = containerHeight;
        const innerRadius = Math.min(width, height) * 0.32;
        const outerRadius = innerRadius + 8;

        const chord = d3.chordDirected()
            .padAngle(10 / innerRadius)
            .sortSubgroups(d3.descending)
            .sortChords(d3.descending);

        const arc = d3.arc()
            .innerRadius(innerRadius)
            .outerRadius(outerRadius);

        const ribbon = d3.ribbon()
            .radius(innerRadius - 1)
            .padAngle(1 / innerRadius);

        const customColors = [
            '#2C3E50',
            '#E74C3C',
            '#27AE60',
            '#8E44AD',
            '#D35400'
        ];

        const svg = d3.select(svgRef.current)
            .attr("viewBox", [-width / 2, -height / 2, width, height])
            .style("width", "100%")
            .style("height", "100%")
            .style("font", "10px sans-serif");

        const chords = chord(matrix);

        const group = svg.append("g")
            .selectAll("g")
            .data(chords.groups)
            .join("g");

        group.append("path")
            .attr("fill", d => customColors[d.index])
            .attr("d", arc as any);

        group.append("text")
            .each(function(d) { (d as CustomChordGroup).angle = (d.startAngle + d.endAngle) / 2; })
            .attr("dy", "0.35em")
            .attr("transform", (d: CustomChordGroup) => {
                const angle = ((d.angle || 0) * 180 / Math.PI - 90);
                const radius = outerRadius + 10;
                return `
                    rotate(${angle})
                    translate(${radius})
                    ${Math.abs(angle) > 90 ? "rotate(180)" : ""}
                `;
            })
            .attr("text-anchor", (d: CustomChordGroup) => {
                const angle = ((d.angle || 0) * 180 / Math.PI - 90);
                return Math.abs(angle) > 90 ? "end" : "start";
            })
            .text(d => displayedCities[d.index]);

        svg.append("g")
            .attr("fill-opacity", 0.85)
            .selectAll("path")
            .data(chords)
            .join("path")
            .style("mix-blend-mode", "multiply")
            .attr("fill", d => customColors[d.target.index])
            .attr("d", ribbon as any)
            .append("title")
            .text(d => 
                `${displayedCities[d.source.index]} → ${displayedCities[d.target.index]}: ${d.source.value.toFixed(3)}`
            );

    }, [correlationData, displayedCities]);

    return (
        <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            padding: '0.5rem',
        }}>
            <Typography variant="h5" sx={{ 
                textAlign: 'center',
                fontSize: '1.1rem',
                mt: 0.5
            }}>
                Son 6 Hafta İller Arası İlişki Ağı
            </Typography>
            <Box sx={{ 
                padding: '0 1rem',
                display: 'flex',
                gap: 2,
                alignItems: 'center'
            }}>
                <FormControl sx={{ minWidth: 200, flex: 1 }}>
                    <InputLabel id="city-select-label">İlleri Seçin (2-5 İl)</InputLabel>
                    <Select
                        labelId="city-select-label"
                        multiple
                        value={selectedCities}
                        onChange={handleCityChange}
                        label="İlleri Seçin (2-5 İl)"
                        size="small"
                    >
                        {desiredCities.map((city) => (
                            <MenuItem 
                                key={city} 
                                value={city}
                                disabled={selectedCities.length >= 5 && !selectedCities.includes(city)}
                            >
                                {city}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button 
                    variant="contained" 
                    onClick={handleApplySelection}
                    disabled={selectedCities.length < 2}
                    size="small"
                    sx={{ 
                        backgroundColor: '#094151',
                        color: "#fff",
                        minWidth: '120px',
                        '&:hover': {
                            backgroundColor: '#022631',
                            color: "#fff",
                            fontWeight: 600,
                        },
                        '&:disabled': {
                            backgroundColor: '#094151',
                            opacity: 0.5
                        }
                    }}
                >
                    Uygula
                </Button>
            </Box>
            <Box sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                mt: -1
            }}>
                <Box sx={{
                    width: '340px',
                    height: '340px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                }}>
                    <svg
                        ref={svgRef}
                        style={{ 
                            width: '100%',
                            height: '100%',
                            position: 'absolute'
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ChordDiagram as React.FC<ChordDiagramProps>;